import { cloneElement, Component, type CSSProperties, type ReactElement, type ReactNode } from 'react';

import { _isEmpty, _isNotBlank, _notNil } from '@/littledash';

import Button, { type ButtonProps } from '@/components/UI/Button/Button';
import Icon from '@/components/UI/Icon/Icon';
import Portal from '@/components/UI/Portal/Portal';

import './SelectDropDown.scss';

type AlignMenu = 'left' | 'right';

interface SelectDropDownPortalProps {
  width: number;
}

interface SelectDropDownChildProps {
  closeMenu?: () => void;
}

interface SelectDropDownProps {
  children: ReactElement<SelectDropDownChildProps>;
  title?: string;
  /** When provided and empty, disables the toggle button. */
  selection?: unknown;
  className?: string;
  alignMenu?: AlignMenu;
  iconOnly?: boolean;
  disabled?: boolean;
  asideText?: ReactNode;
  loading?: boolean;
  loadingText?: string;
  portalProps?: SelectDropDownPortalProps;
  tooltip?: string;
  style?: CSSProperties;
  btnProps?: Partial<ButtonProps>;
  testId?: string;
  /** Close menu on container scroll — portalled menu cannot follow Study Cages table scroll. */
  isInSideStudyTable?: boolean;
}

interface SelectDropDownState {
  isOpen: boolean;
}

/** Scroll does not bubble; capture needed for nested container scroll. */
const CONTAINER_SCROLL_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };
const DOCUMENT_LISTENER_OPTIONS: AddEventListenerOptions = { capture: false, passive: true };

/** Dropdown toggle (optional portal). Closes on Escape, outside click/touch, and scroll. */
class SelectDropDown extends Component<SelectDropDownProps, SelectDropDownState> {
  static defaultProps: Pick<SelectDropDownProps, 'btnProps'> = {
    btnProps: {},
  };

  private wrapperRef: HTMLDivElement | null = null;

  constructor(props: SelectDropDownProps) {
    super(props);
    this.state = {
      isOpen: false,
    };
    this.escFunction = this.escFunction.bind(this);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount(): void {
    document.addEventListener('keydown', this.escFunction, DOCUMENT_LISTENER_OPTIONS);
    document.addEventListener('scroll', this.handleClickOutside, DOCUMENT_LISTENER_OPTIONS);
    document.addEventListener('mousedown', this.handleClickOutside, DOCUMENT_LISTENER_OPTIONS);
    document.addEventListener('touchstart', this.handleClickOutside, DOCUMENT_LISTENER_OPTIONS);
  }

  componentDidUpdate(_prevProps: SelectDropDownProps, prevState: SelectDropDownState): void {
    // Capture scroll only while open (one listener, not one per table row).
    if (this.props.isInSideStudyTable !== true || prevState.isOpen === this.state.isOpen) {
      return;
    }
    if (this.state.isOpen) {
      document.addEventListener('scroll', this.handleClickOutside, CONTAINER_SCROLL_LISTENER_OPTIONS);
    } else {
      document.removeEventListener('scroll', this.handleClickOutside, CONTAINER_SCROLL_LISTENER_OPTIONS);
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.escFunction, DOCUMENT_LISTENER_OPTIONS);
    document.removeEventListener('scroll', this.handleClickOutside, DOCUMENT_LISTENER_OPTIONS);
    document.removeEventListener('scroll', this.handleClickOutside, CONTAINER_SCROLL_LISTENER_OPTIONS);
    document.removeEventListener('mousedown', this.handleClickOutside, DOCUMENT_LISTENER_OPTIONS);
    document.removeEventListener('touchstart', this.handleClickOutside, DOCUMENT_LISTENER_OPTIONS);
  }

  render(): ReactNode {
    const {
      title,
      children,
      selection,
      className = '',
      alignMenu,
      iconOnly = false,
      disabled = false,
      asideText,
      loading,
      loadingText,
      portalProps,
      testId,
    } = this.props;
    const { isOpen } = this.state;
    const hasAsideText = _notNil(asideText);
    const isSelectionEmpty = typeof selection !== 'undefined' && _isEmpty(selection);
    // Button defaults testId to "button"; always pass an explicit id to avoid collisions.
    const buttonTestId = _isNotBlank(testId) ? `${testId}--button` : 'select-drop-down--button';

    return (
      <div
        className={`ui__select-drop-down dib ${hasAsideText ? 'ui__select-drop-down__selection' : ''}`}
        ref={this.setWrapperRef}
        data-testid={testId}
      >
        {hasAsideText ? (
          <div className="ui__select-drop-down__attach-btn dib mid-gray bg-light-gray ph3 ba b--moon-gray f6">
            {asideText}
          </div>
        ) : null}

        {iconOnly === true ? (
          <div
            data-tooltip-id="global-tooltip-id"
            data-tooltip-content={_isNotBlank(title) ? title : 'More actions'}
            className={`${className} ${disabled === true ? 'ui__disabled' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              this.toggleMenu();
            }}
            data-testid="icon-only-dropdown-toggle"
          >
            <Icon icon="options" viewBox="0 0 20 20" width={20} height={20} />
          </div>
        ) : (
          <Button
            tooltip={this.props.tooltip}
            disabled={isSelectionEmpty || disabled === true}
            className={`ui__select-drop-down-btn ${disabled === true ? 'ui__disabled' : ''} ${className} relative`}
            style={this.props.style}
            onClick={() => {
              this.toggleMenu();
            }}
            {...this.props.btnProps}
            loading={loading}
            loadingText={loadingText}
            testId={buttonTestId}
          >
            {title}
            <Icon
              icon="down_triangle"
              width="8"
              height="8"
              className="percentCenterY right-1"
              style={{ pointerEvents: 'none' }}
            />
          </Button>
        )}
        <div className={`menu-align-${alignMenu ?? 'left'} ${iconOnly === true ? 'icon-only' : ''}`}>
          {isOpen ? this.renderChildren(children, portalProps) : null}
        </div>
      </div>
    );
  }

  toggleMenu = (): void => {
    this.setState((prevState) => ({
      isOpen: !prevState.isOpen,
    }));
  };

  closeMenu = (): void => {
    this.setState({ isOpen: false });
  };

  escFunction(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.closeMenu();
    }
  }

  setWrapperRef(node: HTMLDivElement | null): void {
    this.wrapperRef = node;
  }

  handleClickOutside(event: Event): void {
    const { target } = event;
    let insidePortal = false;
    if (target instanceof Element) {
      insidePortal = _notNil(target.closest('.portal-root'));
    }

    if (_notNil(this.wrapperRef) && target instanceof Node && !this.wrapperRef.contains(target) && !insidePortal) {
      this.closeMenu();
    }
  }

  renderChildren = (
    children: ReactElement<SelectDropDownChildProps>,
    portalProps: SelectDropDownPortalProps | undefined
  ): ReactNode => {
    const cloned = cloneElement(children, {
      closeMenu: this.closeMenu,
    });
    const iconWidth = 20;
    if (_notNil(portalProps)) {
      return (
        <Portal
          parentRef={this.wrapperRef}
          offsets={{
            width: portalProps.width,
            left: _notNil(this.props.alignMenu) ? iconWidth : portalProps.width - iconWidth,
            align: this.props.alignMenu,
            top: iconWidth,
          }}
        >
          {cloned}
        </Portal>
      );
    }
    return cloned;
  };
}

export default SelectDropDown;
